import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customerToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCustomerProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCustomerProfile = async () => {
    try {
      const response = await axios.get(`${API}/public/customer/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Only logout if it's a 401 (authentication error) or 403 (forbidden)
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Authentication failed, logging out');
        logout();
      }
      // For other errors (500, network issues), don't logout - might be temporary
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/public/customer/login`, {
        email,
        password
      });
      
      const { token: newToken, customer: customerData } = response.data;
      setToken(newToken);
      setCustomer(customerData);
      localStorage.setItem('customerToken', newToken);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/public/customer/register`, data);
      
      const { token: newToken, customer: customerData } = response.data;
      setToken(newToken);
      setCustomer(customerData);
      localStorage.setItem('customerToken', newToken);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('customerToken');
  };

  const isAuthenticated = () => {
    return !!token && !!customer;
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider
      value={{
        customer,
        token,
        loading,
        login,
        register,
        logout,
        isAuthenticated,
        getAuthHeaders
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
